import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { CalendarClock, Check, Clock, Plus } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import {
  useGetTasks,
  getGetTasksQueryOptions,
} from "@/requests/gen/react-query/task";
import { ParamPagination } from "@/components/ui/param-data-table/param-pagination";
import {
  PermissionEnum,
  TaskEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { RequirePermission } from "@/components/universal/require-permission";
import NotFoundPage from "@/catchall";
import { TaskModal } from "@/components/modals/task-modal";
import { Button } from "@/components/ui/button";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskLookupCard } from "@/components/universal/task-lookup-card";
import { useIsMobile } from "@/hooks/use-mobile";
import { createFileRoute } from "@tanstack/react-router";

const taskSearchParsers = {
  tab: parseAsStringEnum(["past-reminder", "completed", "future"]).withDefault(
    "past-reminder",
  ),
  task_types: parseAsArrayOf(parseAsStringEnum(Object.values(TaskEnum))),
};

const taskSearchAllParsers = {
  ...dataTableSearchParsers,
  ...taskSearchParsers,
};

const loadTaskSearch = createLoader(taskSearchAllParsers);

type TaskSearch = inferParserType<typeof taskSearchAllParsers>;

// Single source of truth for mapping search params -> task query args.
function taskQueryParams(
  p: TaskSearch,
): Parameters<typeof getGetTasksQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    is_deleted: p.is_deleted,
    completed: p.tab === "completed",
    future: p.tab === "future",
    task_type: p.task_types,
  };
}

export const Route = createFileRoute("/_private/task/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetTasksQueryOptions(
        taskQueryParams(loadTaskSearch(location.searchStr)),
      ),
    ),
  component: TaskWrapper,
});

function TaskWrapper() {
  return (
    <RequirePermission
      permission={PermissionEnum.filter_bypass}
      fallback={<NotFoundPage />}
    >
      <TaskLookup />
    </RequirePermission>
  );
}

// Main component
function TaskLookup() {
  const isMobile = useIsMobile();

  const [queryParams, setQueryParams] = useQueryStates(taskSearchAllParsers);

  const taskTypeOptions = [
    {
      items: Object.values(TaskEnum).map((type) => ({
        label: type,
        value: type,
      })),
    },
  ];

  const currentTab = queryParams.tab;

  function setTab(value: string) {
    setQueryParams({ tab: value as TaskSearch["tab"] });
  }

  // Main tasks query
  const tasks = useGetTasks(taskQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  return (
    <div>
      <title>Tasks - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Tasks", link: { to: "/task/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Tasks</CardTitle>
          <CardDescription>
            View your tasks below. You can search, filter, and click to see more
            details.
          </CardDescription>
          <CardAction>
            <TaskModal
              refetch={tasks.refetch}
              trigger={
                <Button variant="default">
                  <Plus />
                  Add Task
                </Button>
              }
            />
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={tasks.isFetching} />
            <Tabs
              defaultValue="past-reminder"
              value={currentTab}
              onValueChange={setTab}
              className="mt-2 md:mt-0 items-center"
            >
              <TabsList>
                <TabsTrigger value="past-reminder">
                  <CalendarClock className="mr-2 h-4 w-4" />
                  Past Reminder
                </TabsTrigger>
                <TabsTrigger value="completed">
                  <Check className="mr-2 h-4 w-4" />
                  Completed
                </TabsTrigger>
                <TabsTrigger value="future">
                  <Clock className="mr-2 h-4 w-4" />
                  Future
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <DropdownFilter
              title="Task Type"
              options={taskTypeOptions}
              values={queryParams.task_types || []}
              onChange={(values) => setQueryParams({ task_types: values })}
            />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <div className="flex flex-col gap-4">
            {tasks.data?.items.map((task, index) => {
              return (
                <TaskLookupCard key={index} task={task} isMobile={isMobile} />
              );
            })}
            <ParamPagination count={tasks.data?.count ?? 0} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
